<template>
  <section
    v-if="variants.v1 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <h2 class="tw-mb-10 tw-max-w-[580px] tw-self-start md:tw-mb-28">{{ vars.titleText }}</h2>

    <!-- Tabs and Content -->
    <div class="tw-flex tw-w-full tw-flex-col tw-gap-x-14 tw-gap-y-4 md:tw-w-full md:tw-flex-row lg:tw-w-[80%]">
      <!-- Tab Titles -->
      <div class="tw-w-full lg:tw-w-1/4">
        <ul>
          <li v-for="(tab, index) in tabs" :key="index" @click="currentTab = tab" class="tw-mb-6 tw-cursor-pointer">
            <h6
              @click="toggleCollapse(index)"
              class="tw-flex tw-items-center tw-justify-between"
              :class="{ 'tw-text-[var(--c-primary)]': currentTab?.title === tab?.title }"
            >
              {{ tab.title }}
            </h6>

            <!-- Mobile View Collapse Content -->
            <div
              class="tw-mt-2 tw-overflow-hidden tw-transition-all tw-duration-500 md:tw-hidden"
              :style="activeTab === index ? { maxHeight: '1000px', opacity: 1 } : { maxHeight: '0px', opacity: 0 }"
            >
              <p>{{ tab.description }}</p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Tab Content for larger screens (md: and up) -->
      <div
        v-if="currentTab"
        class="tw-hidden tw-w-full tw-max-w-[700px] tw-overflow-hidden tw-opacity-100 tw-transition-all tw-duration-500 tw-ease-in-out md:tw-block lg:tw-w-3/4"
      >
        <transition name="fade" mode="out-in">
          <p :key="currentTab.title">{{ currentTab.description }}</p>
        </transition>
      </div>
    </div>
  </section>

  <section
    v-if="variants.v2 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <small v-if="vars.altTitleText" class="tw-self-start"> {{ vars.altTitleText }}</small>
    <h2 class="tw-mb-10 tw-max-w-[580px] tw-self-start md:tw-mb-28">{{ vars.titleText }}</h2>

    <!-- Tabs and Content -->
    <div class="tw-flex tw-w-full tw-flex-col tw-gap-x-14 tw-gap-y-4 md:tw-w-full md:tw-flex-row lg:tw-w-[80%]">
      <!-- Tab Titles -->
      <div class="tw-w-full lg:tw-w-1/4">
        <ul>
          <li v-for="(tab, index) in tabs" :key="index" @click="currentTab = tab" class="tw-mb-6 tw-cursor-pointer">
            <h3
              @click="toggleCollapse(index)"
              class="tw-flex tw-items-center tw-justify-between"
              :class="{ 'tw-text-[var(--c-primary)]': currentTab?.title === tab?.title }"
            >
              {{ tab.title }}
            </h3>

            <!-- Mobile View Collapse Content -->
            <div
              class="tw-mt-2 tw-overflow-hidden tw-transition-all tw-duration-500 md:tw-hidden"
              :style="activeTab === index ? { maxHeight: '1000px', opacity: 1 } : { maxHeight: '0px', opacity: 0 }"
            >
              <library-image :src="tab.photo"></library-image>
              <p>{{ tab.description }}</p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Tab Content for larger screens (md: and up) -->
      <div
        v-if="currentTab"
        class="tw-hidden tw-w-full tw-max-w-[700px] tw-overflow-hidden tw-opacity-100 tw-transition-all tw-duration-500 tw-ease-in-out md:tw-block lg:tw-w-3/4"
      >
        <library-image
          :src="currentTab.photo"
          class="tw-mb-4"
          style="border-radius: var(--rounded)"
          :w-ratio="56"
          :h-ratio="37"
        ></library-image>
        <transition name="fade" mode="out-in">
          <p :key="currentTab.title">{{ currentTab.description }}</p>
        </transition>
      </div>
    </div>
  </section>

  <section
    v-if="variants.v3 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${
      vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'
    }`"
  >
    <h6 v-if="vars.altTitleText" class="tw-self-start tw-text-black">
      {{ vars.altTitleText }}
    </h6>
    <h2 class="tw-mb-10 tw-max-w-[580px] tw-self-start tw-text-black md:tw-mb-28">
      {{ vars.titleText }}
    </h2>

    <!-- Tabs and Content -->
    <div class="tw-flex tw-w-full tw-flex-col tw-gap-x-14 tw-gap-y-4 md:tw-w-full md:tw-flex-row lg:tw-w-[80%]">
      <!-- Tab Titles -->
      <div class="tw-w-full lg:tw-w-1/4">
        <ul>
          <li v-for="(tab, index) in tabs" :key="index" @click="currentTab = tab" class="tw-mb-6 tw-cursor-pointer">
            <h3
              @click="toggleCollapse(index)"
              class="tw-flex tw-items-center tw-justify-between"
              :class="{ 'tw-text-[var(--c-primary)]': currentTab?.title === tab?.title }"
            >
              {{ tab.title }}
            </h3>

            <!-- Mobile View Collapse Content -->
            <div
              class="tw-mt-2 tw-overflow-hidden tw-transition-all tw-duration-500 md:tw-hidden"
              :style="activeTab === index ? { maxHeight: '1000px', opacity: 1 } : { maxHeight: '0px', opacity: 0 }"
            >
              <!-- Eğer video varsa -->
              <library-iframe
                v-if="tab.video"
                :src="tab.video"
                class="tw-mb-4"
                style="border-radius: var(--rounded)"
                :w-ratio="56"
                :h-ratio="37"
              />
              <!-- Yoksa image -->
              <library-image
                v-else-if="tab.photo"
                :src="tab.photo"
                class="tw-mb-4"
                style="border-radius: var(--rounded)"
                :w-ratio="56"
                :h-ratio="37"
              />
              <p class="tw-mt-4">{{ tab.description }}</p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Tab Content for larger screens (md: and up) -->
      <div
        v-if="currentTab"
        class="tw-hidden tw-w-full tw-max-w-[700px] tw-overflow-hidden tw-opacity-100 tw-transition-all tw-duration-500 tw-ease-in-out md:tw-block lg:tw-w-3/4"
      >
        <!-- Eğer video varsa -->
        <library-iframe
          v-if="currentTab.video"
          :src="currentTab.video"
          style="border-radius: var(--rounded)"
          :w-ratio="56"
          :h-ratio="37"
        />
        <!-- Yoksa image -->
        <library-image
          v-else-if="currentTab.photo"
          :src="currentTab.photo"
          style="border-radius: var(--rounded)"
          :w-ratio="56"
          :h-ratio="37"
        />
        <transition name="fade" mode="out-in">
          <p :key="currentTab.title">{{ currentTab.description }}</p>
        </transition>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
export default defineNuxtComponent({
  name: 'DescriptionsBox',

  mixins: [ComponentMixin],

  data() {
    return {
      currentTab: null,
      activeTab: null,
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
      },
    };
  },

  mounted() {
    this.currentTab = this.tabs[0];
    this.activeTab = 0;
  },

  computed: {
    tabs() {
      return this.groupedVariables.tabs;
    },
  },

  methods: {
    toggleCollapse(index) {
      if (this.activeTab === index) {
        this.activeTab = null;
      } else {
        this.activeTab = index;
      }
    },
  },
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
